import React, { useEffect, useState } from "react";
import { Calendar, X, ArrowRight } from "lucide-react";

/**
 * Floating "Get a Free Consultation" CTA.
 * Appears after the user scrolls past ~70% of the page on /pricing,
 * or anywhere else after they spend 25s on the site without converting.
 * Dismissable for the session.
 */
const FloatingConsultCTA = () => {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("kcc_consult_cta_dismissed") === "1") {
      setDismissed(true);
      return;
    }
    const onScroll = () => {
      const path = window.location.pathname;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const scrolled = window.scrollY;
      const ratio = max > 0 ? scrolled / max : 0;
      // Show after 55% on Pricing page, 75% on other pages.
      const threshold = path.startsWith("/pricing") ? 0.55 : 0.75;
      if (ratio > threshold) setShow(true);
    };
    // Fallback time trigger (25s on the site)
    const t = setTimeout(() => setShow(true), 25000);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(t);
    };
  }, []);

  if (dismissed) return null;

  const dismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    sessionStorage.setItem("kcc_consult_cta_dismissed", "1");
    setDismissed(true);
  };

  return (
    <div
      className={`fixed left-1/2 -translate-x-1/2 bottom-6 z-40
        transition-all duration-500
        ${show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10 pointer-events-none"}`}
      data-testid="floating-consult-cta"
      role="region"
      aria-label="Floating free consultation call to action"
    >
      <div className="relative flex items-center gap-4 rounded-full knight-gradient text-white pl-3 pr-2 py-2 shadow-2xl shadow-knight-navy/40 border border-white/10">
        {/* Pulsing emerald dot */}
        <div className="relative flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500/20 ring-1 ring-emerald-400/40">
          <span className="absolute inset-0 rounded-full bg-emerald-400/40 animate-ping" />
          <Calendar size={16} className="relative text-emerald-200" />
        </div>

        <div className="hidden sm:flex flex-col leading-tight pr-1">
          <span className="text-[10px] tracking-[0.22em] uppercase text-knight-gold font-bold">Still thinking?</span>
          <span className="text-sm font-bold whitespace-nowrap">Get a Free Consultation</span>
        </div>
        <span className="sm:hidden text-sm font-bold whitespace-nowrap pr-1">Free Consultation</span>

        <a
          href="https://zcal.co/knightmediagroup/kcc1"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="floating-consult-book-btn"
          className="inline-flex items-center gap-1 bg-knight-gold hover:bg-amber-400 text-knight-navy font-bold rounded-full px-4 py-2 text-sm transition-colors hover:-translate-y-[1px]"
        >
          Book now <ArrowRight size={14} />
        </a>

        <button
          onClick={dismiss}
          aria-label="Dismiss free consultation banner"
          data-testid="floating-consult-dismiss"
          className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors ml-0.5"
        >
          <X size={13} />
        </button>
      </div>
    </div>
  );
};

export default FloatingConsultCTA;
